/**
 * Mail Config Page - Admin-Oberfläche für Microsoft Graph Konfiguration
 * Nur sichtbar für UMitza
 * BUILD: 2025-01-25-v1 - FIX Empty State zeigt jetzt Init-Button
 */

import { useState, useEffect, useCallback } from 'react';
import { Mail, Settings, CheckCircle, XCircle, RefreshCw, Play, Square, Clock, AlertTriangle, Eye, EyeOff, Calendar, AlertCircle, Server } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BUILD_VERSION = "2.2.8-2025-12-28"; // Archive+Draft Graph-API, Chat Mail-Integration

export const MailConfigPage = ({ user }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // NEU: Separater Error-State für Laden
  const [initializing, setInitializing] = useState(false); // NEU: Für Init-Button
  const [testingId, setTestingId] = useState(null);
  const [backfillingId, setBackfillingId] = useState(null); // NEU: Für Backfill-Button
  const [monitoringActionId, setMonitoringActionId] = useState(null); // NEU: Für Monitoring-Button
  const [syncingId, setSyncingId] = useState(null); // NEU: Für Sync-Button
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [apiDebug, setApiDebug] = useState(null); // NEU: Debug-Info

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser).token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setApiDebug(null);
    
    try {
      const token = getToken();
      
      // Debug: Token vorhanden?
      if (!token) {
        setLoadError("Kein Auth-Token gefunden. Bitte erneut einloggen.");
        setApiDebug({ error: "NO_TOKEN", url: `${API}/mail/accounts` });
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/mail/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Debug: API Response
      setApiDebug({
        status: response.status,
        url: `${API}/mail/accounts`,
        accountsCount: response.data.accounts?.length || 0
      });
      
      setAccounts(response.data.accounts || []);
      
      // WICHTIG: Bei leerer Liste KEIN loadError setzen!
      // loadError ist nur für echte API-Fehler (401, 500, etc.)
      // Der "Empty State" wird über accounts.length === 0 erkannt
      
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      
      // Debug: Error Details
      setApiDebug({
        error: true,
        status: status,
        url: `${API}/mail/accounts`,
        detail: detail
      });
      
      if (status === 401) {
        setLoadError("Nicht authentifiziert (401). Bitte erneut einloggen.");
      } else if (status === 403) {
        setLoadError("Keine Berechtigung (403). Nur UMitza/Admin haben Zugriff.");
      } else if (status === 404) {
        setLoadError("API-Endpunkt nicht gefunden (404). Backend evtl. veraltet.");
      } else if (status === 500) {
        setLoadError(`Server-Fehler (500): ${detail}`);
      } else {
        setLoadError(`Fehler beim Laden: ${detail}`);
      }
      
      setMessage({ type: 'error', text: detail });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // NEU: Manuelle Initialisierung der Mail-Konten
  const initAccounts = async () => {
    setInitializing(true);
    setMessage({ type: '', text: '' });
    
    try {
      const token = getToken();
      const response = await axios.post(`${API}/mail/accounts/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ 
        type: 'success', 
        text: response.data.message || 'Mail-Konten erfolgreich initialisiert'
      });
      
      // Neu laden
      await loadAccounts();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Initialisierung fehlgeschlagen'
      });
    } finally {
      setInitializing(false);
    }
  };

  const handleTestConnection = async (accountId) => {
    setTestingId(accountId);
    setMessage({ type: '', text: '' });
    
    try {
      const token = getToken();
      const response = await axios.post(`${API}/mail/accounts/${accountId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
      
      // Reload to get updated status
      await loadAccounts();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Verbindungstest fehlgeschlagen' });
    } finally {
      setTestingId(null);
    }
  };

  const handleEdit = (account) => {
    setEditingId(account.id);
    setEditForm({
      client_id: account.client_id || '',
      client_secret: '',
      tenant_id: account.tenant_id || '',
      enabled: account.enabled || false,
      secret_expires_at: account.secret_expires_at || ''
    });
  };

  const handleSave = async (accountId) => {
    try {
      const token = getToken();
      const formData = new FormData();
      
      if (editForm.client_id) formData.append('client_id', editForm.client_id);
      if (editForm.client_secret) formData.append('client_secret', editForm.client_secret);
      if (editForm.tenant_id) formData.append('tenant_id', editForm.tenant_id);
      formData.append('enabled', editForm.enabled.toString());
      formData.append('secret_expires_at', editForm.secret_expires_at || '');

      await axios.put(`${API}/mail/accounts/${accountId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Konfiguration gespeichert' });
      setEditingId(null);
      await loadAccounts();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Speichern fehlgeschlagen' });
    }
  };

  // Backfill starten (24 Monate Mails abrufen)
  const handleStartBackfill = async (accountId) => {
    setBackfillingId(accountId);
    setMessage({ type: '', text: '' });
    
    try {
      const token = getToken();
      const response = await axios.post(`${API}/mail/accounts/${accountId}/backfill`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Backfill gestartet: ${response.data.message || 'Mails werden abgerufen...'}` 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Backfill konnte nicht gestartet werden' 
        });
      }
      
      await loadAccounts();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Backfill fehlgeschlagen' 
      });
    } finally {
      setBackfillingId(null);
    }
  };

  // Monitoring starten/stoppen
  const handleToggleMonitoring = async (accountId, currentlyActive) => {
    setMonitoringActionId(accountId);
    setMessage({ type: '', text: '' });
    
    const action = currentlyActive ? 'stop' : 'start';
    const expectedNewState = !currentlyActive;
    
    try {
      const token = getToken();
      const response = await axios.post(`${API}/mail/accounts/${accountId}/monitoring/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: expectedNewState 
            ? `Monitoring gestartet. ${response.data.initial_sync ? 'Erster Sync läuft...' : ''}` 
            : 'Monitoring gestoppt'
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Aktion fehlgeschlagen' 
        });
      }
      
      await loadAccounts();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Monitoring-Änderung fehlgeschlagen' 
      });
    } finally {
      setMonitoringActionId(null);
    }
  };

  // Sofortiger Sync (manuell)
  const handleSyncNow = async (accountId, forceFullSync = false) => {
    setSyncingId(accountId);
    setMessage({ type: '', text: '' });
    
    try {
      const token = getToken();
      const url = forceFullSync 
        ? `${API}/mail/accounts/${accountId}/sync-now?force_full=true`
        : `${API}/mail/accounts/${accountId}/sync-now`;
      
      const response = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const inbox = response.data.inbox || {};
        const sent = response.data.sent_items?.new_messages || 0;
        
        let statusText = `Sync abgeschlossen: ${inbox.new_messages || 0} neue Mails`;
        if (inbox.skipped_duplicates > 0) {
          statusText += ` (${inbox.skipped_duplicates} bereits vorhanden)`;
        }
        if (inbox.total_fetched > 0) {
          statusText += ` - ${inbox.total_fetched} geprüft`;
        }
        if (sent > 0) {
          statusText += `, ${sent} gesendet`;
        }
        if (inbox.errors && inbox.errors.length > 0) {
          statusText += ` ⚠️ ${inbox.errors.length} Fehler`;
        }
        
        setMessage({ 
          type: inbox.error ? 'error' : 'success', 
          text: statusText
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Sync fehlgeschlagen' 
        });
      }
      
      await loadAccounts();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Sync fehlgeschlagen' 
      });
    } finally {
      setSyncingId(null);
    }
  };

  // Prüft ob Secret in den nächsten 30 Tagen abläuft
  const isSecretExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const expDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isSecretExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const expDate = new Date(expiresAt);
    const now = new Date();
    return Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
            <CheckCircle className="h-3 w-3" />
            Verbunden
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-xs font-medium">
            <XCircle className="h-3 w-3" />
            Fehler
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium">
            <AlertTriangle className="h-3 w-3" />
            Nicht konfiguriert
          </span>
        );
    }
  };

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <RefreshCw className="h-10 w-10 animate-spin text-blue-500" />
            <p className="text-gray-600 dark:text-gray-400">Lade Mail-Konten...</p>
            <p className="text-xs text-gray-400">Build: {BUILD_VERSION}</p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================
  if (loadError && accounts.length === 0) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center">
              <Settings className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Mail-Konfiguration</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Build: {BUILD_VERSION}</p>
            </div>
          </div>
          
          {/* Error Card */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-800 dark:text-red-300 mb-2">
                  Fehler beim Laden der Mail-Konten
                </h3>
                <p className="text-red-700 dark:text-red-400 mb-4">{loadError}</p>
                
                {/* Debug Info */}
                {apiDebug && (
                  <div className="bg-red-100 dark:bg-red-900/40 rounded-lg p-3 text-xs font-mono text-red-800 dark:text-red-300 mb-4">
                    <p><strong>API URL:</strong> {apiDebug.url}</p>
                    <p><strong>Status:</strong> {apiDebug.status || apiDebug.error}</p>
                    {apiDebug.detail && <p><strong>Detail:</strong> {apiDebug.detail}</p>}
                  </div>
                )}
                
                <button
                  onClick={loadAccounts}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // EMPTY STATE (keine Accounts in DB)
  // ============================================
  if (accounts.length === 0) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center">
              <Settings className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Mail-Konfiguration</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Build: {BUILD_VERSION}</p>
            </div>
          </div>
          
          {/* Empty State Card */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Server className="h-8 w-8 text-yellow-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                  Keine Mail-Konten gefunden
                </h3>
                <p className="text-yellow-700 dark:text-yellow-400 mb-4">
                  Die Mail-Konten sollten beim Server-Start automatisch erstellt werden. 
                  Dies kann passieren wenn:
                </p>
                <ul className="list-disc list-inside text-yellow-700 dark:text-yellow-400 mb-4 text-sm space-y-1">
                  <li>Die Datenbank leer ist (neues Deployment)</li>
                  <li>Der Backend-Server noch nicht vollständig gestartet ist</li>
                  <li>Ein Fehler beim Initialisieren aufgetreten ist</li>
                </ul>
                
                {/* Debug Info */}
                {apiDebug && (
                  <div className="bg-yellow-100 dark:bg-yellow-900/40 rounded-lg p-3 text-xs font-mono text-yellow-800 dark:text-yellow-300 mb-4">
                    <p><strong>API URL:</strong> {apiDebug.url}</p>
                    <p><strong>Status:</strong> {apiDebug.status}</p>
                    <p><strong>Accounts:</strong> {apiDebug.accountsCount}</p>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={initAccounts}
                    disabled={initializing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    {initializing ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Initialisiere...
                      </>
                    ) : (
                      <>
                        <Server className="h-4 w-4" />
                        Mail-Konten initialisieren
                      </>
                    )}
                  </button>
                  <button
                    onClick={loadAccounts}
                    disabled={loading}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Erneut laden
                  </button>
                </div>
                
                {message.text && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${
                    message.type === 'success' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {message.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // NORMAL STATE (Accounts vorhanden)
  // ============================================
  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center">
            <Settings className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mail-Konfiguration</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Microsoft Graph Anbindung für E-Mail-Postfächer
              <span className="ml-2 text-xs text-gray-400">(Build: {BUILD_VERSION})</span>
            </p>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' 
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Setup Guide */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Einrichtungshinweis
          </h3>
          <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">
            Für die Anbindung benötigen Sie eine Azure App Registration mit folgenden Berechtigungen:
          </p>
          <ul className="text-sm text-blue-600 dark:text-blue-400 list-disc list-inside space-y-1">
            <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">Mail.Read</code> - Mails lesen</li>
            <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">Mail.ReadWrite</code> - Mails verwalten</li>
            <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">Mail.Send</code> - Mails senden/weiterleiten</li>
            <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">User.Read.All</code> - Benutzerinfo</li>
          </ul>
        </div>

        {/* Account Cards */}
        <div className="space-y-4">
          {accounts.map((account) => (
            <div 
              key={account.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-4 md:p-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <Mail className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {account.display_name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {account.email}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(account.connection_status)}
                </div>

                {/* Status Info */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Letzter Sync:</span>
                    <p className="text-gray-900 dark:text-white">{formatDate(account.last_sync)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Backfill:</span>
                    <p className="text-gray-900 dark:text-white">
                      {account.backfill_completed ? '✅ Abgeschlossen' : '⏳ Ausstehend'}
                    </p>
                  </div>
                </div>

                {account.last_error && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300">
                    <strong>Letzter Fehler:</strong> {account.last_error}
                  </div>
                )}

                {/* Secret Expiry Warning */}
                {account.secret_expires_at && (
                  isSecretExpired(account.secret_expires_at) ? (
                    <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
                      <XCircle className="h-5 w-5 flex-shrink-0" />
                      <div>
                        <strong>Secret abgelaufen!</strong> Das Client Secret ist seit dem{' '}
                        {new Date(account.secret_expires_at).toLocaleDateString('de-DE')} abgelaufen.
                        Bitte erneuern Sie es im Azure Portal.
                      </div>
                    </div>
                  ) : isSecretExpiringSoon(account.secret_expires_at) ? (
                    <div className="mb-4 p-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                      <div>
                        <strong>Secret läuft bald ab!</strong> Noch {getDaysUntilExpiry(account.secret_expires_at)} Tage
                        (bis {new Date(account.secret_expires_at).toLocaleDateString('de-DE')}).
                        Bitte rechtzeitig erneuern.
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                      <Calendar className="h-4 w-4 flex-shrink-0" />
                      <span>Secret gültig bis: {new Date(account.secret_expires_at).toLocaleDateString('de-DE')}</span>
                    </div>
                  )
                )}

                {/* Edit Form */}
                {editingId === account.id ? (
                  <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Client ID (Application ID)
                      </label>
                      <input
                        type="text"
                        value={editForm.client_id}
                        onChange={(e) => setEditForm({ ...editForm, client_id: e.target.value })}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Client Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets[account.id] ? 'text' : 'password'}
                          value={editForm.client_secret}
                          onChange={(e) => setEditForm({ ...editForm, client_secret: e.target.value })}
                          placeholder="Neues Secret eingeben (leer lassen um beizubehalten)"
                          className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets({ ...showSecrets, [account.id]: !showSecrets[account.id] })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets[account.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tenant ID (Directory ID)
                      </label>
                      <input
                        type="text"
                        value={editForm.tenant_id}
                        onChange={(e) => setEditForm({ ...editForm, tenant_id: e.target.value })}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <Calendar className="h-4 w-4 inline mr-1" />
                        Secret gültig bis
                      </label>
                      <input
                        type="date"
                        value={editForm.secret_expires_at ? editForm.secret_expires_at.split('T')[0] : ''}
                        onChange={(e) => setEditForm({ ...editForm, secret_expires_at: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Datum aus Azure Portal (Certificates &amp; Secrets → Expiration)
                      </p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.enabled}
                        onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Konto aktiviert</span>
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave(account.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                      >
                        Speichern
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Actions */
                  <div className="flex flex-wrap gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <button
                      onClick={() => handleEdit(account)}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
                    >
                      <Settings className="h-4 w-4" />
                      Konfigurieren
                    </button>
                    <button
                      onClick={() => handleTestConnection(account.id)}
                      disabled={testingId === account.id}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      {testingId === account.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Verbindung testen
                    </button>
                    {!account.backfill_completed && (
                      <button
                        onClick={() => handleStartBackfill(account.id)}
                        disabled={account.connection_status !== 'connected' || backfillingId === account.id}
                        className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
                          account.connection_status === 'connected' 
                            ? 'bg-orange-600 text-white hover:bg-orange-700' 
                            : 'bg-orange-600 text-white opacity-50 cursor-not-allowed'
                        }`}
                        title={account.connection_status !== 'connected' ? 'Erst nach erfolgreicher Verbindung verfügbar' : 'Einmaliger Abruf der letzten 24 Monate'}
                      >
                        {backfillingId === account.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Clock className="h-4 w-4" />
                        )}
                        {backfillingId === account.id ? 'Backfill läuft...' : 'Backfill (24 Monate)'}
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleMonitoring(account.id, account.live_monitoring_active)}
                      disabled={account.connection_status !== 'connected' || monitoringActionId === account.id}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
                        account.connection_status === 'connected'
                          ? account.live_monitoring_active 
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-green-600 text-white opacity-50 cursor-not-allowed'
                      }`}
                      title={account.connection_status !== 'connected' ? 'Erst nach erfolgreicher Verbindung verfügbar' : ''}
                    >
                      {monitoringActionId === account.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : account.live_monitoring_active ? (
                        <Square className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {monitoringActionId === account.id 
                        ? 'Wird geändert...' 
                        : account.live_monitoring_active 
                          ? 'Monitoring stoppen' 
                          : 'Monitoring starten'}
                    </button>
                    {/* Manueller Sync-Button */}
                    <button
                      onClick={() => handleSyncNow(account.id, false)}
                      disabled={account.connection_status !== 'connected' || syncingId === account.id}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
                        account.connection_status === 'connected'
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-purple-600 text-white opacity-50 cursor-not-allowed'
                      }`}
                      title="Neue Mails seit letztem Sync abrufen"
                    >
                      {syncingId === account.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      {syncingId === account.id ? 'Sync läuft...' : 'Sync'}
                    </button>
                    {/* Force Full Sync Button */}
                    <button
                      onClick={() => handleSyncNow(account.id, true)}
                      disabled={account.connection_status !== 'connected' || syncingId === account.id}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
                        account.connection_status === 'connected'
                          ? 'bg-orange-600 text-white hover:bg-orange-700'
                          : 'bg-orange-600 text-white opacity-50 cursor-not-allowed'
                      }`}
                      title="Letzte 24h komplett neu laden (bei fehlenden Mails)"
                    >
                      <RefreshCw className="h-4 w-4" />
                      24h Sync
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
